import { useEffect, useRef } from 'react';
import { audioEngine } from '@/lib/audio/AudioEngine';
import type { Phase } from './useTrainingEngine';
import type { CueId } from '@/lib/audio/types';

const COMMAND_TO_CUE: Record<string, CueId> = {
    'Stance': 'stance',
    'Motion': 'motion',
    'Circle': 'circle',
    'Level Change': 'level_change',
    'Sprawl': 'sprawl',
    'Shot': 'shot',
};

export function useStanceMotionAudio(
    phase: Phase,
    isRunning: boolean,
    enabled: boolean,
    commands: string[] = ['Stance', 'Motion', 'Circle', 'Level Change', 'Sprawl', 'Shot']
) {
    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        if (!enabled || phase !== 'work' || !isRunning) {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        const scheduleNext = () => {
            // Random interval between 2.5s and 5.5s
            const delay = 2500 + Math.random() * 3000;

            timeoutRef.current = window.setTimeout(() => {
                // Pick random command
                const cmd = commands[Math.floor(Math.random() * commands.length)];
                if (cmd) {
                    const cueId = COMMAND_TO_CUE[cmd];
                    if (cueId) {
                        audioEngine.playCue(cueId);
                    } else {
                        // Fallback to TTS if cue mapping is missing
                        audioEngine.speak(cmd);
                    }
                }
                scheduleNext();
            }, delay);
        };

        scheduleNext();

        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, [phase, isRunning, enabled, commands]);
}
